import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomerService } from "../../services/CustomerService";
import { Show, SimpleShowLayout, useRecordContext } from "react-admin";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  TextField,
} from "@mui/material";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CustomerCookPreferences from "./CustomerCookPref";

const customerService = new CustomerService();

const CustomerPreferences = (props: any) => {
 let record = useRecordContext();
   let [loading, setLoading] = useState(false);
   let [customerPreference, setCustomerPreference] = useState<any>(null);
   let [blockedCooks, setBlockedCooks] = useState<any[]>([]);
   let [prefState, setPrefState] = useState<any>({});
   let [blacklistInput, setBlacklistInput] = useState("");


   const refreshData = useCallback(
       () => {
       setLoading(true);
       Promise.all([
           customerService.getCustomerPrefernceByCustomerId(Number(record?.id)).catch(() => null),
           customerService.getBlockedCooksByCustomerId(Number(record?.id)).catch(() => []),
       ]).then(([prefData, blockedData]) => {
           setCustomerPreference(prefData);
           setBlockedCooks(blockedData ?? []);
           setLoading(false);
       }).catch(() => {
           setLoading(false);
       });
       },
       [record?.id]
   );

     useEffect(() => {
       refreshData();
     }, [refreshData]);

     useEffect(() => {
       setPrefState({
         genderPref: customerPreference?.genderPref ?? "",
         religionPref: customerPreference?.religionPref ?? "",
         foodTypePref: customerPreference?.foodTypePref ?? "",
         multidayPref: customerPreference?.multidayPref ?? "",
       });
     }, [customerPreference]);

     const isDirty = useMemo(() => {
       if (!customerPreference) {
         // No existing preference — enable if user has selected anything
         return !!(prefState?.genderPref || prefState?.religionPref || prefState?.foodTypePref || prefState?.multidayPref);
       }
       return (
         prefState?.genderPref !== customerPreference?.genderPref ||
         prefState?.religionPref !== customerPreference?.religionPref ||
         prefState?.foodTypePref !== customerPreference?.foodTypePref ||
         prefState?.multidayPref !== customerPreference?.multidayPref
       );
     }, [prefState, customerPreference]);

     const handlePrefChange = (key: string, value: string) => {
       setPrefState((prev: any) => ({
         ...prev,
         [key]: value,
       }));
     };

  return (
    <>
    <Show {...props} actions={false}>
      <SimpleShowLayout>
        <Box sx={{ width: "100%" }}>
          {loading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Preferences
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 4 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Gender preference
                    </Typography>
                    <ToggleButtonGroup
                      value={prefState?.genderPref ?? ""}
                      exclusive
                      onChange={(e, val) => val && handlePrefChange("genderPref", val)}
                      size="small"
                      sx={{
                        gap: 1,
                        flexWrap: "wrap",
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "999px !important",
                          px: 2,
                          textTransform: "none",
                          color: "text.primary",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                        },
                        "& .MuiToggleButton-root.Mui-selected:hover": {
                          bgcolor: "primary.dark",
                        },
                      }}
                    >
                      <ToggleButton value="MALE">Male</ToggleButton>
                      <ToggleButton value="FEMALE">Female</ToggleButton>
                      <ToggleButton value="ANY">Any</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Religion preference
                    </Typography>
                    <ToggleButtonGroup
                      value={prefState?.religionPref ?? ""}
                      exclusive
                      onChange={(e, val) => val && handlePrefChange("religionPref", val)}
                      size="small"
                      sx={{
                        gap: 1,
                        flexWrap: "wrap",
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "999px !important",
                          px: 2,
                          textTransform: "none",
                          color: "text.primary",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                        },
                        "& .MuiToggleButton-root.Mui-selected:hover": {
                          bgcolor: "primary.dark",
                        },
                      }}
                    >
                      <ToggleButton value="NON_MUSLIM">Non-Muslim</ToggleButton>
                      <ToggleButton value="ANY">Any</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Food type preference
                    </Typography>
                    <ToggleButtonGroup
                      value={prefState?.foodTypePref ?? ""}
                      exclusive
                      onChange={(e, val) => val && handlePrefChange("foodTypePref", val)}
                      size="small"
                      sx={{
                        gap: 1,
                        flexWrap: "wrap",
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "999px !important",
                          px: 2,
                          textTransform: "none",
                          color: "text.primary",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                        },
                        "& .MuiToggleButton-root.Mui-selected:hover": {
                          bgcolor: "primary.dark",
                        },
                      }}
                    >
                      <ToggleButton value="VEG_ONLY">Veg only</ToggleButton>
                      <ToggleButton value="BOTH">Any</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Backup option on selected cook unavailability
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <RadioGroup
                  value={prefState?.multidayPref ?? ""}
                  onChange={(e) => handlePrefChange("multidayPref", e.target.value)}
                >
                  <FormControlLabel
                    value="Cancel visit if preferred chef unavailable"
                    control={<Radio />}
                    label="Cancel visit if preferred chef unavailable"
                  />
                  <FormControlLabel
                    value="Allocate any backup chef at scheduled time"
                    control={<Radio />}
                    label="Allocate any backup chef at scheduled time"
                  />
                  <FormControlLabel
                    value="Confirm time change, then allocate preferred chef"
                    control={<Radio />}
                    label="Confirm time change, then allocate preferred chef"
                  />
                </RadioGroup>
              </Paper>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  disabled={!isDirty}
                  onClick={() => {
                    customerService
                      .updateCustomerPrefernceByCustomerId(Number(record?.id), {
                        genderPref: prefState?.genderPref,
                        religionPref: prefState?.religionPref,
                        foodTypePref: prefState?.foodTypePref,
                        multidayPref: prefState?.multidayPref,
                      })
                      .then(() => {
                        refreshData();
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Updated preferences successfully",
                        type: CustomSnackbarTypes.SUCCESS,
                        });
                      })
                      .catch((err) => {
                        console.error("Error updating preferences:", err);
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Error updating preferences",
                        type: CustomSnackbarTypes.ERROR,
                        });
                      });
                  }}
                >
                  Update preferences
                </Button>
              </Box>
            </>
          )}
        </Box>
      </SimpleShowLayout>
      <SimpleShowLayout>
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Block Cook

                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", gap: 2, width: "100%", flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                        id="outlined-basic"
                        variant="outlined"
                        size="small"
                        sx={{ width: { xs: "100%", sm: "50%" } }}
                        placeholder="Enter cook Ids like 4182, 4765, 9876"
                        value={blacklistInput}
                        onChange={(e) => setBlacklistInput(e.target.value)} 
                    />
                    <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        disabled ={blacklistInput.trim() === ""}
                        onClick={() => {
                        customerService.addToBlacklist(Number(record?.id), blacklistInput).then(()=>{    
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Cook added to black list successfully",
                            type: CustomSnackbarTypes.SUCCESS,
                        });
                        setBlacklistInput("");
                        refreshData();
                    }).catch(error=>{
                        console.log(error);
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Error adding cook to black list",
                            type: CustomSnackbarTypes.ERROR,
                        });
                    })
                    }}
                        
                    >
                        Block Cook For Customer
                    </Button>
                </Box>
                <Box sx={{ mt: 2 , width: '50%'}}>
                    {blockedCooks.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cook ID</TableCell>
                                        <TableCell>Cook Name</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {blockedCooks.map((cook: any) => {
                                        const cookName = [
                                            cook?.cook?.firstName ?? cook?.cookFullProfile?.firstName,
                                            cook?.cook?.lastName ?? cook?.cookFullProfile?.lastName,
                                        ]
                                            .filter(Boolean)
                                            .join(" ");
                                        return (
                                            <TableRow key={cook.cookId}>
                                                <TableCell>{cook?.cookId ?? "-"}</TableCell>
                                                <TableCell>{cookName || "-"}</TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => {
                                                            customerService.removeFromPrefernce(Number(cook.id)).then(()=>{
                                                                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                                                    message: "Cook removed from black list successfully",
                                                                    type: CustomSnackbarTypes.SUCCESS,
                                                                });
                                                                refreshData();
                                                            }).catch(error=>{
                                                                console.log(error);
                                                                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                                                    message: "Error removing cook from black list",
                                                                    type: CustomSnackbarTypes.ERROR,
                                                                });
                                                            })
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No blacklisted cooks found.
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
      </SimpleShowLayout>
      <CustomerCookPreferences customerId={record?.id} />
    </Show>
    </>
  );
};

export default CustomerPreferences;
